import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Box,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { postSupportCommand } from 'apis/supportAPIs';

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  iconRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  fieldGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  actions: {
    marginTop: 'auto',
    padding: theme.spacing(2),
  },
}));

const FetchTransactions = ({ restaurantId, deviceId, onCommandStart }) => {
  const classes = useStyles();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [format, setFormat] = useState('csv');
  const [loading, setLoading] = useState(false);

  const handleRun = useCallback(async () => {
    setLoading(true);
    try {
      const { commandId } = await postSupportCommand({
        restaurantId,
        deviceId,
        type: 'fetchTransactions',
        params: {
          startDate,
          endDate: endDate || startDate,
          format,
        },
      });
      onCommandStart(commandId, 'fetchTransactions');
    } catch {
      onCommandStart(null, 'fetchTransactions');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, deviceId, startDate, endDate, format, onCommandStart]);

  const isValid = startDate.length > 0;

  const maxRange = () => {
    if (!startDate) return undefined;
    const d = new Date(startDate);
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  };

  return (
    <Card className={classes.card} aria-label="Fetch Transactions" role="region">
      <CardContent>
        <div className={classes.iconRow}>
          <ReceiptLongIcon color="primary" />
          <Typography variant="h6">Fetch Transactions</Typography>
        </div>
        <div className={classes.fieldGroup}>
          <TextField
            label="Start date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            inputProps={{ 'aria-label': 'Start date' }}
          />
          <TextField
            label="End date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            inputProps={{
              min: startDate,
              max: maxRange(),
              'aria-label': 'End date',
            }}
          />
        </div>
        <Box>
          <Typography variant="body2" gutterBottom>
            Format
          </Typography>
          <ToggleButtonGroup
            value={format}
            exclusive
            onChange={(_, v) => v && setFormat(v)}
            size="small"
            aria-label="Export format"
          >
            <ToggleButton value="csv" aria-label="CSV format">
              CSV
            </ToggleButton>
            <ToggleButton value="json" aria-label="JSON format">
              JSON
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </CardContent>
      <CardActions className={classes.actions}>
        <Button
          variant="contained"
          onClick={handleRun}
          disabled={!isValid || loading}
          fullWidth
        >
          {loading ? 'Sending…' : 'Run'}
        </Button>
      </CardActions>
    </Card>
  );
};

FetchTransactions.propTypes = {
  restaurantId: PropTypes.string.isRequired,
  deviceId: PropTypes.string.isRequired,
  onCommandStart: PropTypes.func.isRequired,
};

export default FetchTransactions;
